import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdEdit, MdDelete, MdAdd, MdExpandMore, MdExpandLess, MdSave } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { clearIndustriesCache, fetchIndustries as fetchIndustriesForImages } from 'services/industryService';

interface SubIndustry {
    name: string;
    sub?: string[];
    priceMin?: number;
    priceMax?: number;
    imageUrl?: string;
    // Stable numeric "product id" for the generic seller API (POST /lead/api/submit) — see
    // industrySchema.ts on the backend. Editable from the create/edit form (form.tsx).
    subIndustryId?: number;
}

interface Industry {
    _id: string;
    name: string;
    description?: string;
    forWho?: string;
    pricing: string[];
    fields: string[];
    mandatoryFields: string[];
    steps?: number;
    subIndustries: SubIndustry[];
    createdAt: string;
    updatedAt: string;
}

type AvgPrices = Record<string, { avg: number; count: number }>;

export default function BackOfficeIndustries() {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [avgPrices, setAvgPrices] = useState<AvgPrices>({});
    const [overrideInputs, setOverrideInputs] = useState<Record<string, Record<string, { min: string; max: string; imageUrl: string }>>>({});
    const [savingPrices, setSavingPrices] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchIndustries();
        fetchAvgPrices();
    }, []);

    const fetchIndustries = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/industry`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIndustries(response.data);
            const inputs: Record<string, Record<string, { min: string; max: string; imageUrl: string }>> = {};
            for (const ind of response.data as Industry[]) {
                inputs[ind._id] = {};
                for (const sub of ind.subIndustries) {
                    inputs[ind._id][sub.name] = {
                        min: sub.priceMin != null ? String(sub.priceMin) : '',
                        max: sub.priceMax != null ? String(sub.priceMax) : '',
                        imageUrl: sub.imageUrl ?? '',
                    };
                }
            }
            setOverrideInputs(inputs);
        } catch (error) {
            console.error('Error fetching industries:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvgPrices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/industry/avg-prices`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvgPrices(response.data);
        } catch (error) {
            console.error('Error fetching avg prices:', error);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${CONFIG.apiUrl}/industry/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIndustries(industries.filter(ind => ind._id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting industry:', error);
            alert('Failed to delete industry');
        }
    };

    const toggleRow = (id: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleOverrideChange = (industryId: string, subName: string, field: 'min' | 'max' | 'imageUrl', value: string) => {
        setOverrideInputs(prev => ({
            ...prev,
            [industryId]: {
                ...prev[industryId],
                [subName]: { ...prev[industryId]?.[subName], [field]: value },
            },
        }));
    };

    const savePriceOverrides = async (industry: Industry) => {
        setSavingPrices(industry._id);
        try {
            const token = localStorage.getItem('token');
            const updatedSubIndustries = industry.subIndustries.map(sub => {
                const range = overrideInputs[industry._id]?.[sub.name];
                const parsedMin = range?.min !== '' && range?.min != null ? parseFloat(range.min) : undefined;
                const parsedMax = range?.max !== '' && range?.max != null ? parseFloat(range.max) : undefined;
                const imageUrl = range?.imageUrl?.trim();
                return {
                    ...sub,
                    priceMin: parsedMin != null && !isNaN(parsedMin) ? parsedMin : undefined,
                    priceMax: parsedMax != null && !isNaN(parsedMax) ? parsedMax : undefined,
                    imageUrl: imageUrl ? imageUrl : undefined,
                };
            });
            await axios.put(
                `${CONFIG.apiUrl}/industry/${industry._id}`,
                { ...industry, subIndustries: updatedSubIndustries },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setIndustries(prev =>
                prev.map(ind =>
                    ind._id === industry._id ? { ...ind, subIndustries: updatedSubIndustries } : ind
                )
            );
            clearIndustriesCache();
            fetchIndustriesForImages().catch(() => {});
        } catch (error) {
            console.error('Error saving price overrides:', error);
            alert('Failed to save price overrides');
        } finally {
            setSavingPrices(null);
        }
    };

    const filteredIndustries = industries.filter((industry) => {
        return (
            industry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            industry.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Industries Management
                    </div>
                    <button
                        onClick={() => navigate('/back-office/industries/create')}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                        <MdAdd className="text-lg" />
                        Add Industry
                    </button>
                </div>

                <div className="mb-5">
                    <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MdSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search industries..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Name
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Sub-Industries
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Fields
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredIndustries.map((industry) => (
                                    <React.Fragment key={industry._id}>
                                        <tr className="border-b border-gray-100 dark:border-navy-700">
                                            <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                                                {industry.name}
                                            </td>
                                            <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                                {industry.subIndustries.length > 0 ? (
                                                    <button
                                                        onClick={() => toggleRow(industry._id)}
                                                        className="flex items-center gap-1 rounded px-2 py-1 text-brand-500 hover:bg-gray-100 dark:hover:bg-navy-700"
                                                    >
                                                        {industry.subIndustries.length} sub-industries
                                                        {expandedRows.has(industry._id) ? (
                                                            <MdExpandLess />
                                                        ) : (
                                                            <MdExpandMore />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <span>0 sub-industries</span>
                                                )}
                                            </td>
                                            <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                                {industry.fields.length + industry.mandatoryFields.length} fields
                                            </td>
                                            <td className="py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => navigate(`/back-office/industries/edit/${industry._id}`)}
                                                        className="rounded-lg bg-blue-500 p-2 text-white hover:bg-blue-600"
                                                        title="Edit"
                                                    >
                                                        <MdEdit />
                                                    </button>
                                                    {deleteConfirm === industry._id ? (
                                                        <div className="flex gap-1">
                                                            <button
                                                                onClick={() => handleDelete(industry._id)}
                                                                className="rounded bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600"
                                                            >
                                                                Confirm
                                                            </button>
                                                            <button
                                                                onClick={() => setDeleteConfirm(null)}
                                                                className="rounded bg-gray-500 px-3 py-1 text-xs text-white hover:bg-gray-600"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setDeleteConfirm(industry._id)}
                                                            className="rounded-lg bg-red-500 p-2 text-white hover:bg-red-600"
                                                            title="Delete"
                                                        >
                                                            <MdDelete />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {expandedRows.has(industry._id) && industry.subIndustries.length > 0 && (
                                            <tr className="bg-gray-50 dark:bg-navy-900">
                                                <td colSpan={4} className="px-4 pb-4 pt-2">
                                                    <div className="rounded-lg border border-gray-200 dark:border-navy-600 overflow-hidden">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="bg-gray-100 dark:bg-navy-800 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                                    <th className="px-4 py-2 text-left">Sub-industry</th>
                                                                    <th className="px-4 py-2 text-left">API id</th>
                                                                    <th className="px-4 py-2 text-left">Real avg price (leads)</th>
                                                                    <th className="px-4 py-2 text-left">Override price (admin)</th>
                                                                    <th className="px-4 py-2 text-left">Image URL</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {industry.subIndustries.map((sub) => {
                                                                    const computed = avgPrices[sub.name];
                                                                    return (
                                                                        <tr
                                                                            key={sub.name}
                                                                            className="border-t border-gray-100 dark:border-navy-700"
                                                                        >
                                                                            <td className="px-4 py-2 font-medium text-navy-700 dark:text-white">
                                                                                {sub.name}
                                                                            </td>
                                                                            <td className="px-4 py-2 font-mono text-gray-500 dark:text-gray-400">
                                                                                {sub.subIndustryId ?? <span className="italic text-gray-400">none</span>}
                                                                            </td>
                                                                            <td className="px-4 py-2 text-gray-400 dark:text-gray-500">
                                                                                {computed ? (
                                                                                    <span title={`Based on ${computed.count} lead(s)`}>
                                                                                        {computed.avg.toFixed(2)} € <span className="text-xs">({computed.count} leads)</span>
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="italic">No data yet</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-2">
                                                                                <div className="flex items-center gap-1 flex-wrap">
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="0.01"
                                                                                        placeholder="Min"
                                                                                        value={overrideInputs[industry._id]?.[sub.name]?.min ?? ''}
                                                                                        onChange={(e) =>
                                                                                            handleOverrideChange(industry._id, sub.name, 'min', e.target.value)
                                                                                        }
                                                                                        className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                                                                    />
                                                                                    <span className="text-xs text-gray-400">–</span>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="0.01"
                                                                                        placeholder="Max"
                                                                                        value={overrideInputs[industry._id]?.[sub.name]?.max ?? ''}
                                                                                        onChange={(e) =>
                                                                                            handleOverrideChange(industry._id, sub.name, 'max', e.target.value)
                                                                                        }
                                                                                        className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                                                                    />
                                                                                    {(sub.priceMin != null || sub.priceMax != null) && (
                                                                                        <span className="text-xs text-brand-500 whitespace-nowrap">
                                                                                            Saved: {sub.priceMin ?? '?'} – {sub.priceMax ?? '?'} €
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-4 py-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    {overrideInputs[industry._id]?.[sub.name]?.imageUrl && (
                                                                                        <img
                                                                                            src={overrideInputs[industry._id][sub.name].imageUrl}
                                                                                            alt=""
                                                                                            className="h-8 w-8 flex-shrink-0 rounded object-cover"
                                                                                            onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                                                                        />
                                                                                    )}
                                                                                    <input
                                                                                        type="text"
                                                                                        placeholder="https://.../image.png (S3 link)"
                                                                                        value={overrideInputs[industry._id]?.[sub.name]?.imageUrl ?? ''}
                                                                                        onChange={(e) =>
                                                                                            handleOverrideChange(industry._id, sub.name, 'imageUrl', e.target.value)
                                                                                        }
                                                                                        className="w-56 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                                                                    />
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        <div className="flex justify-end px-4 py-2 bg-gray-50 dark:bg-navy-900 border-t border-gray-200 dark:border-navy-600">
                                                            <button
                                                                onClick={() => savePriceOverrides(industry)}
                                                                disabled={savingPrices === industry._id}
                                                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                                                            >
                                                                <MdSave />
                                                                {savingPrices === industry._id ? 'Saving…' : 'Save prices'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        {filteredIndustries.length === 0 && (
                            <div className="py-10 text-center text-gray-500">
                                No industries found
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
